import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { getModelInfo, getModelAccuracy, downloadModelPdf, downloadTechnicalDoc } from '../lib/api';
import { DataLoader } from '../components/ForecastLoader';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Brain, 
  Calendar, 
  ArrowRight, 
  CheckCircle2, 
  Target,
  TrendingUp,
  Database,
  Cpu,
  Layers,
  Settings,
  Zap,
  Download,
  FileText,
  Activity,
  Award
} from 'lucide-react';

const ModeloPage = () => {
  const [data, setData] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingTech, setDownloadingTech] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [infoResponse, accuracyResponse] = await Promise.all([
          getModelInfo(),
          getModelAccuracy()
        ]);
        setData(infoResponse.data);
        setAccuracy(accuracyResponse.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Modelo de Forecasting</h1>
            <p className="text-slate-500 mt-1">Cargando información del modelo...</p>
          </div>
          <DataLoader type="model" />
        </div>
      </Layout>
    );
  }

  const models = data?.models || [];
  const dataFlow = data?.data_flow || [];
  const ensemble = data?.ensemble || {};
  const features = data?.features_used || [];
  const techSpecs = data?.technical_specs || {};
  const roadmap = data?.roadmap_phase2 || [];
  const dataSources = data?.data_sources || [];

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const response = await downloadModelPdf();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'modelo_forecasting_documentacion.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadTechnicalDoc = async () => {
    setDownloadingTech(true);
    try {
      const response = await downloadTechnicalDoc();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'documentacion_tecnica_forecasting.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading technical doc:', error);
    } finally {
      setDownloadingTech(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="modelo-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Modelo de Forecasting</h1>
            <p className="text-slate-500 mt-1">Sistema de predicción basado en Machine Learning</p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={handleDownloadTechnicalDoc} 
              disabled={downloadingTech}
              variant="outline"
              className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
            >
              {downloadingTech ? (
                <>
                  <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mr-2" />
                  Generando...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Documento Técnico
                </>
              )}
            </Button>
            <Button 
              onClick={handleDownloadPdf} 
              disabled={downloading}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {downloading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Generando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Descargar PDF
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Accuracy Metrics Section */}
        {accuracy && accuracy.mape !== null && (
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Award className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Precisión del Modelo</h2>
                <p className="text-sm text-white/80">Métricas calculadas con backtesting (últimos {accuracy.test_months} meses)</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* MAPE */}
              <div className="bg-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white/70">MAPE</span>
                  <Activity className="w-4 h-4 text-white/50" />
                </div>
                <p className="text-3xl font-bold">{accuracy.mape}%</p>
                <p className="text-xs text-white/60 mt-1">Error Porcentual Absoluto Medio</p>
              </div>
              
              {/* Interpretation */}
              <div className="bg-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white/70">Calificación</span>
                  <CheckCircle2 className="w-4 h-4 text-white/50" />
                </div>
                <p className="text-xl font-bold">{accuracy.interpretation}</p>
                <p className="text-xs text-white/60 mt-1">
                  {accuracy.mape < 10 ? 'MAPE < 10%' : accuracy.mape < 20 ? 'MAPE < 20%' : 'MAPE ≥ 20%'}
                </p>
              </div>
              
              {/* RMSE */}
              <div className="bg-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white/70">RMSE</span>
                  <TrendingUp className="w-4 h-4 text-white/50" />
                </div>
                <p className="text-2xl font-bold">{accuracy.rmse?.toLocaleString()}</p>
                <p className="text-xs text-white/60 mt-1">Error Cuadrático Medio (kgs)</p>
              </div>
              
              {/* MAE */}
              <div className="bg-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white/70">MAE</span>
                  <Target className="w-4 h-4 text-white/50" />
                </div>
                <p className="text-2xl font-bold">{accuracy.mae?.toLocaleString()}</p>
                <p className="text-xs text-white/60 mt-1">Error Absoluto Medio (kgs)</p>
              </div>
            </div>
            
            {/* Monthly Detail */}
            {accuracy.monthly_detail && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-sm text-white/70 mb-3">Detalle por mes (backtesting):</p>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                  {accuracy.monthly_detail.map((m) => (
                    <div key={m.month} className="bg-white/10 rounded-lg p-2 text-center">
                      <p className="text-xs text-white/60">{m.month}</p>
                      <p className={`text-sm font-semibold ${m.error_pct > 15 ? 'text-amber-300' : 'text-white'}`}>
                        {m.error_pct}% error
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Model Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {models.map((model, i) => (
            <div 
              key={model.name}
              className={`rounded-2xl p-6 ${
                i === 0 
                  ? 'bg-gradient-to-br from-indigo-500 to-violet-600 text-white' 
                  : 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {i === 0 ? (
                    <Cpu className="w-8 h-8" />
                  ) : (
                    <Calendar className="w-8 h-8" />
                  )}
                  <div>
                    <h2 className="text-xl font-bold">{model.name}</h2>
                    <p className="text-white/70 text-sm">Peso: {model.weight}% | v{model.version}</p>
                  </div>
                </div>
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold">{model.weight}%</span>
                </div>
              </div>
              <p className="text-white/90 mb-4">{model.description}</p>
              <div className="space-y-2">
                {model.strengths.map((strength, j) => (
                  <div key={j} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-white/70" />
                    <span className="text-white/90">{strength}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Ensemble Method */}
        {ensemble.method && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 text-white">
            <div className="flex items-center gap-3 mb-3">
              <Layers className="w-6 h-6" />
              <h2 className="text-lg font-semibold">Método Ensemble: {ensemble.method}</h2>
            </div>
            <p className="text-white/90">{ensemble.description}</p>
          </div>
        )}

        {/* Data Sources */}
        {dataSources.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <Database className="w-5 h-5 text-slate-400" />
              Fuentes de Datos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {dataSources.map((source, i) => (
                <div key={i} className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-5 border border-slate-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      i === 0 ? 'bg-indigo-100 text-indigo-600' :
                      i === 1 ? 'bg-emerald-100 text-emerald-600' :
                      'bg-amber-100 text-amber-600'
                    }`}>
                      {i === 0 ? <TrendingUp className="w-5 h-5" /> :
                       i === 1 ? <Target className="w-5 h-5" /> :
                       <Zap className="w-5 h-5" />}
                    </div>
                    <h3 className="font-semibold text-slate-900">{source.name}</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">{source.description}</p>
                  <p className="text-xs text-slate-500 bg-white rounded-lg p-2 border border-slate-200">
                    <span className="font-medium">Uso:</span> {source.usage}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Data Flow */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
            <ArrowRight className="w-5 h-5 text-slate-400" />
            Pipeline de Procesamiento
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {dataFlow.map((step, i) => (
              <div 
                key={i}
                className="relative bg-slate-50 rounded-xl p-4 border border-slate-200"
              >
                <div className="absolute -top-3 -left-3 w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {i + 1}
                </div>
                <p className="text-sm text-slate-700 pl-4">{step}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Features Used */}
        {features.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <Zap className="w-5 h-5 text-slate-400" />
              Features Utilizados
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {features.map((feature, i) => (
                <div key={i} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <p className="font-medium text-slate-900">{feature.name}</p>
                  <p className="text-sm text-slate-500 mt-1">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Technical Specs */}
        {techSpecs.xgboost && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <Settings className="w-5 h-5 text-slate-400" />
              Especificaciones Técnicas
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200">
                <h3 className="font-semibold text-indigo-900 mb-3">XGBoost</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Estimadores:</span>
                    <span className="font-mono text-indigo-700">{techSpecs.xgboost.n_estimators}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Max Depth:</span>
                    <span className="font-mono text-indigo-700">{techSpecs.xgboost.max_depth}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Learning Rate:</span>
                    <span className="font-mono text-indigo-700">{techSpecs.xgboost.learning_rate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Objective:</span>
                    <span className="font-mono text-indigo-700">{techSpecs.xgboost.objective}</span>
                  </div>
                </div>
              </div>
              <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
                <h3 className="font-semibold text-emerald-900 mb-3">Prophet</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Yearly Seasonality:</span>
                    <span className="font-mono text-emerald-700">{techSpecs.prophet.yearly_seasonality ? 'True' : 'False'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Seasonality Mode:</span>
                    <span className="font-mono text-emerald-700">{techSpecs.prophet.seasonality_mode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Changepoint Prior:</span>
                    <span className="font-mono text-emerald-700">{techSpecs.prophet.changepoint_prior_scale}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* How It Works */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 text-white">
          <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
            <Brain className="w-6 h-6" />
            ¿Cómo funciona?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/10 rounded-xl p-5">
              <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center mb-4">
                <span className="text-lg font-bold">1</span>
              </div>
              <h3 className="font-semibold mb-2">Ingesta de Datos</h3>
              <p className="text-sm text-white/70">
                Se procesan <b>36 meses de historial</b> de volumen por cliente 
                y las <b>oportunidades del pipeline</b> con su probabilidad de cierre.
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-5">
              <div className="w-10 h-10 bg-violet-500 rounded-lg flex items-center justify-center mb-4">
                <span className="text-lg font-bold">2</span>
              </div>
              <h3 className="font-semibold mb-2">Feature Engineering</h3>
              <p className="text-sm text-white/70">
                Se crean features: <b>lags</b> (1,3,6,12 meses), <b>medias móviles</b>, 
                <b> desviación estándar</b>, <b>factores estacionales</b> por mes, 
                trimestre y tendencia temporal.
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-5">
              <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center mb-4">
                <span className="text-lg font-bold">3</span>
              </div>
              <h3 className="font-semibold mb-2">Entrenamiento ML</h3>
              <p className="text-sm text-white/70">
                <b>XGBoost</b> (100 estimadores, depth=5) detecta patrones no lineales. 
                <b> Prophet</b> captura estacionalidad anual/mensual y tendencias a largo plazo.
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-5">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                <span className="text-lg font-bold">4</span>
              </div>
              <h3 className="font-semibold mb-2">Ensemble + Pipeline</h3>
              <p className="text-sm text-white/70">
                Se combinan predicciones (<b>60% XGBoost + 40% Prophet</b>). 
                Se suman <b>oportunidades × probabilidad</b> al mes estimado. 
                Se calculan <b>intervalos de confianza 80%</b>.
              </p>
            </div>
          </div>
          <p className="text-xs text-white/50 mt-4 text-center">
            * Los datos Penta se utilizan para calcular Share of Wallet y Cobertura de Mercado, no alimentan directamente el forecast.
          </p>
        </div>

        {/* Roadmap */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-slate-400" />
            Hoja de Ruta - Fase 2
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {roadmap.map((item, i) => (
              <div 
                key={i}
                className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200"
              >
                <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center shrink-0">
                  <Target className="w-4 h-4 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">{item}</p>
                  <p className="text-xs text-slate-500 mt-1">Próximamente</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ModeloPage;
